import { Wand2, Sparkles, Activity, Pause, Zap } from 'lucide-react';
import { PageTour } from '@/components/common/PageTour';

/**
 * 5-step Banking Auto-Rules walkthrough. Steps cover:
 *   1. What auto-rules ARE
 *   2. Suggest from history (one-click setup)
 *   3. Test before you save
 *   4. Sparkline + Pause for seasonal vendors
 *   5. Replay over old transactions
 *
 * Triggered by the page-level info callout's "Show me around" button.
 * Props: open, onOpenChange, onJumpToSuggest (optional finish-flow hook).
 */
const STEPS = [
  {
    icon: Wand2,
    title: '1. What is an auto-rule?',
    body: (
      <>
        An auto-rule turns recurring bank-statement lines into the right entry automatically. Example: every line containing
        {' '}<code className="bg-violet-100 px-1 rounded">STC</code> is a Telecom expense; every line containing
        {' '}<code className="bg-violet-100 px-1 rounded">Saudi Aramco</code> is Fuel. Once a rule exists, the
        <strong> Replay &amp; apply</strong> button categorises matching txns into expenses for you — no manual entry.
      </>
    ),
  },
  {
    icon: Sparkles,
    title: '2. Let the suggester seed your rules',
    body: (
      <>
        First-time setup is one click. Hit <strong>Suggest from history</strong> in the page header — we scan the last
        90 days, cluster recurring vendors (handling typos like &quot;GOOGEL&quot; via Levenshtein matching), and propose the top
        candidates with a confidence score and editable category. Tick the ones you want and save.
      </>
    ),
  },
  {
    icon: Zap,
    title: '3. Test before you commit',
    body: (
      <>
        Inside the create-rule dialog there&apos;s a <strong>Test</strong> button that dry-runs the rule against the last 30
        days and shows exactly which lines would match. Tweak the
        {' '}<code className="bg-violet-100 px-1 rounded">match_value</code> until you&apos;re happy, then save. No data is
        modified during a test.
      </>
    ),
  },
  {
    icon: Activity,
    title: '4. Track activity with the sparkline',
    body: (
      <>
        Each rule shows a 30-day sparkline of when it last fired. Hover any bar to see the date and count. When a rule
        starts misfiring (e.g. the vendor restructured their billing) you&apos;ll see it on the
        {' '}<strong>Top movers</strong> panel at the top of the page. Use the
        {' '}<Pause size={11} className="inline mx-0.5" /> <strong>Pause</strong> button to stop a seasonal rule for 30
        days without losing its history.
      </>
    ),
  },
  {
    icon: Zap,
    title: '5. Replay over old transactions',
    body: (
      <>
        New rules don&apos;t just affect the future. Click <strong>Dry-run last 30 days</strong> to preview how many old txns
        a rule would have caught, then <strong>Replay &amp; apply</strong> to retroactively create those expenses. Already-linked
        txns are skipped, so it&apos;s safe to run repeatedly.
      </>
    ),
  },
];

export function BankingRulesTour({ open, onOpenChange, onJumpToSuggest }) {
  return (
    <PageTour
      open={open}
      onOpenChange={onOpenChange}
      steps={STEPS}
      tourName="Banking Auto-Rules walkthrough"
      testidPrefix="banking-tour"
      onFinish={onJumpToSuggest}
      finishLabel="Try it now"
    />
  );
}
